import { IconName, library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Switch, Text, Tooltip } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import { ComponentProps, useEffect } from "react";
import { useStopwatch } from "react-timer-hook";
import { toast } from "react-toastify";

import { useSession } from "@/hooks/@custom/useSession";
import { useGetQueueDelayStatusByCompanyQuery, useToggleQueueDelayMutation } from "@/store/apis/nextcargo";
import { cn } from "@/utils/cn";

library.add(fas);

function calculateElapsedTime(createdAt: string | Date, delayedTime: string): { elapsed: dayjs.Dayjs; isExpired: boolean } {
  const created = dayjs(createdAt);
  const now = dayjs();

  const [delayHours, delayMinutes] = delayedTime.split(":").map(Number);
  const delayTotalMinutes = delayHours * 60 + delayMinutes;

  const elapsedSeconds = Math.max(now.diff(created, "second"), 0);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);

  const isExpired = elapsedMinutes >= delayTotalMinutes;

  const elapsedTime = dayjs().startOf("day").add(elapsedSeconds, "second");

  return { elapsed: elapsedTime, isExpired };
}

export function DelayDialog() {
  const [isAutoEndEnabled, { toggle: toggleAutoEndDelay, close: disableAutoEndDelay }] = useDisclosure();

  const { expedition } = useSession();
  const companyId = expedition?.company.id;

  const { data: delayData } = useGetQueueDelayStatusByCompanyQuery({ id: companyId! }, { skip: !companyId });
  const [trigger, { isLoading }] = useToggleQueueDelayMutation();

  const delayName = delayData?.delay?.reason || "Atraso Desconhecido";
  const delayIconName = delayData?.delay?.icon as IconName;
  const delayedTime = delayData?.delayedTime; // Formato é "HH:mm"
  const delayCreatedAt = delayData?.created_at;

  const { elapsed: elapsedTime, isExpired } = calculateElapsedTime(delayCreatedAt!, delayedTime || "00:00");
  const totalHours = elapsedTime.diff(dayjs().startOf("day"), "hour");
  const elapsedLabel = totalHours >= 1 ? elapsedTime.format("HH[h]:mm[m]:ss[s]") : elapsedTime.format("mm[m]:ss[s]");
  const time = elapsedTime.format("HH:mm");

  const { start, pause, reset } = useStopwatch({ autoStart: false });
  const isMobile = useMediaQuery("(max-width: 768px)");

  const dialogProps = new Map<boolean, Omit<ComponentProps<typeof Dialog>, "opened">>([
    [
      true,
      {
        size: "sm",
        position: { left: "50%", bottom: "sm" },
        classNames: { root: cn("-translate-x-1/2") }
      }
    ],
    [
      false,
      {
        size: "md",
        position: { left: "md", bottom: "md" }
      }
    ]
  ]);

  const handleCancelDelay = async (time: string) => {
    disableAutoEndDelay();
    return await trigger({ companyId: companyId!, delayId: "", time: `-${time}` });
  };

  useEffect(() => {
    if (isExpired && isAutoEndEnabled && time === delayedTime) {
      (async () => {
        const promise = handleCancelDelay(time);
        toast.promise(promise, {
          pending: "Encerrando atraso automaticamente...",
          success: "Atraso encerrado automaticamente!",
          error: "Erro ao encerrar atraso"
        });
      })();
    }
  }, [isExpired, isAutoEndEnabled, time, delayedTime]);

  useEffect(() => {
    if (delayData?.active) {
      start();
    } else {
      pause();
    }

    return reset;
  }, [delayData?.active]);

  return (
    <Dialog opened={!!delayData?.active} classNames={{ root: cn("flex! flex-col! gap-2!") }} withCloseButton={false} color="red" {...dialogProps.get(isMobile)}>
      <>
        <div className="flex items-center justify-start gap-2 md:gap-2">
          <div className="grid size-12 place-items-center rounded-full bg-red-500">
            {delayIconName && <FontAwesomeIcon icon={["fas", delayIconName]} color="white" size="lg" enableBackground="jk" />}
          </div>

          <strong className="text-lg font-semibold text-red-800">{delayName}</strong>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col text-2xl font-bold text-red-700">
            <div>
              <time>{elapsedLabel}</time>
            </div>

            <div className="flex flex-col gap-2">
              <Text size="sm" ta={{ sm: "center", md: "left" }} fw="normal" lh="normal" c="red">
                Por favor, encerre o atraso para voltar ao atendimento
              </Text>

              {!isExpired && (
                <Tooltip label="Quando o tempo definido para o atraso expirar automaticamente irá encerrar o atraso" color="red" w={220} ta="center" multiline withArrow>
                  <Switch
                    label={`Desabilitar atraso ao expirar (${delayedTime?.split(":")[0]}h ${delayedTime?.split(":")[1]}m)`}
                    onChange={toggleAutoEndDelay}
                    checked={isAutoEndEnabled}
                    color="red"
                    withThumbIndicator={false}
                    onLabel="On"
                    offLabel="Off"
                  />
                </Tooltip>
              )}
            </div>
          </div>

          <Button size="sm" color="red.8" radius="sm" className="uppercase!" onClick={() => handleCancelDelay(time)} loading={isLoading} fullWidth>
            Encerrar Atraso
          </Button>
        </div>
      </>
    </Dialog>
  );
}
